import * as React from 'react';
import PropTypes from 'prop-types';
import { useMediaQuery } from '@mui/material';
import { Ic } from '../shell/Icons';
import { planDrop } from '../tree/RequirementsTreeGrid';
import { usePref } from '../../services/prefs';
import { APPROVAL_TONE_CLASS, APPROVAL_DOT_GLYPH } from '../../utils/statusTone';
import { itemHashHref } from '../../utils/itemId';
import { isPlainLeftClick } from '../../utils/navClick';
import useAutoFocus from '../../hooks/useAutoFocus';

// Frozen module-level singletons for empty default props (#943,
// react/no-object-type-as-default-prop). A `= []` / `= {}` default builds a NEW
// value on every render, so a memoised child or an effect dependency sees a change
// that never happened.
const EMPTY_ARRAY = Object.freeze([]);

// Same mobile breakpoint as the rest of the app (screens.css / ImpactGraph.js's
// MOBILE_QUERY) — see the `draggable` note below for why it's read here.
const MOBILE_QUERY = '(max-width:640px)';

/**
 * Requirements tree — matches the agreed mockup (docs/design/specimen/mockup.html
 * `.treebox`/`.trow`) while keeping the existing behaviours: single + multi-select
 * (ctrl/⌘ toggle, shift range), expand/collapse, row + background context menu,
 * drag-and-drop re-parent/reorder (planDrop), text/type filtering, and
 * double-click inline rename.
 */

// #569: the tree dot used to read the free-text `status`/`verification_status`
// attribute (the old "Verify" concept) — it now reflects the APPROVAL rollup
// (`item_approvals`, fetched in bulk by the caller via `GET
// /items/approval-states` and passed in as `approvalStates` — see
// RequirementsTreeView.js) so a real reviewer decision drives the dot, not a
// free-text field anyone could edit. No entry in `approvalStates` (not yet
// fetched, or the item has no approval history) renders no dot at all —
// distinct from `pending` (an explicit "no approvals recorded yet" rollup,
// still rendered muted) only in that the fetch hasn't landed.
function approvalDot(item, approvalStates) {
  const rec = approvalStates && approvalStates[item.id];
  if (!rec) return null;
  return APPROVAL_TONE_CLASS[rec.state] || null;
}

const APPROVAL_DOT_LABEL = {
  approved: 'Approved',
  not_approved: 'Not approved',
  pending: 'Pending approval',
  // F-09 / REQ-6099 (#603, D-08): the sign-off superseded another reviewer's live
  // rejection. Both decisions are retained on the record, so the dot must say so
  // rather than reading as an ordinary approval.
  approved_over_objection: 'Approved over another reviewer’s objection',
};

// F-11 / REQ-6101 (#603): `stale` now covers a lapsed REJECTION as well as a lapsed
// approval, so this label may no longer assume "Approved". `stale_state` comes from
// `GET /items/approval-states` (comments_api.py, via `fb.stale_decision_kind`); the
// fallback when it is absent is NEUTRAL by design — guessing "Approved" here is exactly
// the mislabelling this requirement exists to remove.
const APPROVAL_STALE_LABEL = {
  approved: 'Approved · stale (content changed since approval)',
  not_approved: 'Not approved · stale (content changed since the rejection)',
  mixed: 'Decisions stale (content changed since they were recorded)',
};

// F-61 / REQ-6119 (#606, D-10, #629): "same annotation as `/breakdown`, so the tree
// dot's surface can carry 'user inactive' too" — the field was added to `GET
// /items/approval-states` (comments_api.py) for exactly this surface; leaving it
// unread here would be the #581 drift (an annotation present on one surface, absent
// on another) this whole programme exists to remove. The state/tone is UNCHANGED
// (D-10: the approval stands) — this only appends to the text/aria-label channel.
function reviewerInactiveSuffix(item, approvalStates) {
  const rec = approvalStates && approvalStates[item.id];
  return rec && rec.reviewer_inactive ? ' · user inactive' : '';
}

function approvalTitleFor(item, approvalStates) {
  const rec = approvalStates && approvalStates[item.id];
  if (!rec) return undefined;
  const suffix = reviewerInactiveSuffix(item, approvalStates);
  if (rec.state === 'stale') {
    return (APPROVAL_STALE_LABEL[rec.stale_state]
      || 'Decision stale (content changed since it was recorded)') + suffix;
  }
  return (APPROVAL_DOT_LABEL[rec.state] || 'Pending approval') + suffix;
}

// F-23 / REQ-6109 (#607, WCAG 1.4.1 "Use of Colour"): the dot must be
// distinguishable WITHOUT colour — a colour-blind user can't tell approved from
// not-approved by hue alone, and a screen reader gets nothing from a bare
// coloured span. `APPROVAL_DOT_GLYPH` (utils/statusTone.js) is shared with the
// item-card dot and the link-type drawer's row status so every surface agrees on
// what each state looks like, not just what it's called. `aria-label` (not just
// `title`) carries the accessible name.

function humanId(item) {
  const id = String(item.requirement_id || item.human_id || item.display_id || item.id || '');
  if (/^[A-Za-z]{2,6}-\d+/.test(id)) return id;
  return id.length > 9 ? `${id.slice(0, 7)}…` : id;
}

// A node can be expanded if it explicitly reports children (lazy `has_children`), already
// has some loaded, is a container by type, or is a virtual project group node.
function hasKids(it) {
  if (typeof it.has_children === 'boolean') return it.has_children || (it.children || []).length > 0;
  return (it.children || []).length > 0;
}
function isContainerItem(it) {
  return it._kind === 'project' || it.attributes?.type === 'container' || it.type === 'container' || hasKids(it);
}

// Archived items carry a soft-delete marker from the backend when include_archived is
// requested (#349). Detect it defensively across the shapes the API may use.
function isArchivedItem(it) {
  if (!it) return false;
  const st = (it.status || it.attributes?.status || '').toString().toLowerCase();
  return it.archived === true || Boolean(it.deleted_at) || it.attributes?.archived === true || st === 'archived';
}

export default function RequirementsTree({
  items, selected = EMPTY_ARRAY, expanded = EMPTY_ARRAY, onSelectedChange, onExpandedChange, onItemClick,
  onContextMenu, onMoveItem, onBackgroundContextMenu, filterText = '', typeFilter = null, onRename,
  onLoadChildren, onLoadMore, loadingIds = null, searchActive = false, openCommentItems = null,
  reviewFilter = null, eager = false, approvalStates = null,
  // #1092: exact item-type id filter (from `?type=`); `eagerCollapsedIds` keeps named
  // branches (other projects under `&scope=all`) closed while an eager filter is on.
  typeIdFilter = null, eagerCollapsedIds = EMPTY_ARRAY,
  // Branches whose child load failed, and how to retry one. Without these a failed
  // branch renders an indefinite "…" that reads as "empty" (owner report, 2026-07-28).
  failedChildIds = null, onRetryChildren = null,
  // singleSelect (picker mode): a plain click always replaces the selection; ctrl/⌘
  // and shift multi/range-select are disabled so a modal picker can only pick ONE item.
  singleSelect = false,
  // #817: publish the VISIBLE row order so ItemDetail's Prev/Next can follow what the
  // user can actually see — filters applied, collapsed branches excluded. The tree
  // already computes this for its own roving tabindex (`treeRows`); nothing else in the
  // app can reconstruct it, because it depends on expansion and filter state that live
  // here. Called with an array of ids, only when the order actually changes.
  onVisibleOrderChange = null,
}) {
  const anchorRef = React.useRef(null);
  // #408: drop feedback carries WHERE relative to the hovered row: 'inside' (nest
  // into a container — folder highlight), or 'before'/'after' (reorder — an insertion
  // line at the row's top/bottom edge). Was a bare id with only a full-row outline.
  const [dropInfo, setDropInfo] = React.useState(null);
  const [editingId, setEditingId] = React.useState(null);
  const [editValue, setEditValue] = React.useState('');
  // #793: ONE ref shared by whichever row is currently the inline editor — at
  // most one row is ever `editing` at a time, so only that row's `<input>`
  // attaches this ref (see the `editing ?` branch below). Keyed on `editingId`
  // itself rather than a plain boolean so starting a NEW edit while a
  // different row's edit is committing still re-focuses (the dependency value
  // changes either way). A hook cannot be called per-row inside the `.map()`
  // below, hence hoisting it here rather than following the usual
  // one-hook-per-site pattern used elsewhere for #793.
  const editFocusRef = useAutoFocus(editingId);
  // #169: roving-tabindex active node for the ARIA "tree" keyboard pattern. Exactly one
  // visible treeitem carries tabIndex=0 (the rest -1); Arrow keys move it. `null` until
  // the user focuses/keyboards — `effectiveActiveId` (below) falls back to the selection
  // or the first row so the tree always has one reachable tab stop.
  const [activeId, setActiveId] = React.useState(null);
  // #169: DOM nodes for each visible treeitem, so keyboard navigation can move focus.
  const rowRefs = React.useRef(new Map());
  const showHumanIds = usePref('showHumanIds');
  const statusDots = usePref('statusDots');
  // Native HTML5 drag-and-drop (`draggable`) has no touch equivalent — it never
  // actually worked as a drag gesture on mobile — but SETTING the attribute still
  // has a real side effect there: mobile Chromium/WebKit must disambiguate a touch
  // starting on a `draggable` element as "maybe a native drag" before it can commit
  // to a scroll, which was silently swallowing every touch-scroll/pinch-zoom
  // gesture that started on a row (reported: "can't scroll at all in the tree
  // view on mobile"). Since mobile gets none of the benefit, only the cost, drop
  // `draggable` entirely below the app's mobile breakpoint.
  const isMobile = useMediaQuery(MOBILE_QUERY);
  const rawId = (item) => { const s = String(item.id || ''); return s.length > 9 ? `${s.slice(0, 7)}…` : s; };

  const q = (filterText || '').trim().toLowerCase();
  const typeActive = Array.isArray(typeFilter) && typeFilter.length > 0;
  // #350/#349: review-state filter — each outstanding state can be toggled separately
  // ('archive_proposed', 'needs_review' = open review comments, 'unverified' leaves,
  // 'archived'). An empty/absent list means "no review filter".
  const reviewActive = Array.isArray(reviewFilter) && reviewFilter.length > 0;
  const typeIdActive = Boolean(typeIdFilter);
  const filterActive = q.length > 0 || typeActive || reviewActive || typeIdActive;

  // Does an item match any of the currently-selected review states?
  // F-21/F-22 / REQ-6107/REQ-6108 (#581): the 'unverified' bucket used to read the
  // retired free-text status attribute; it now reads the approval rollup
  // (`approvalStates`, the SAME bulk-fetched map the dot uses — see
  // RequirementsTreeView.js). Its MEANING is now PENDING ONLY (never reviewed — a
  // direct successor of the old bucket's intent). INT-5: the backend's server-side
  // eager prefetch (`GET /items/review-filter`) now derives from the SAME rollup, so
  // the two agree; 'unverified' is retained here (and server-side) purely as a
  // back-compat alias for the renamed 'unapproved'.
  // 'not_approved' (actively rejected) is a NEW, separate, distinguishable state —
  // per the owner's resolution on #581, "never reviewed" and "rejected" must not
  // collapse into one filter. 'unapproved' is accepted as a forward-compat alias
  // for the day the backend key is actually renamed. `archive_proposed` is a
  // legitimate, unrelated status-attribute value (#581 audit's "verified clean"
  // list) and keeps reading the attribute directly. No entry in `approvalStates`
  // yet (not fetched) never matches — consistent with `approvalDot`'s own
  // "no entry != pending" rule below.
  const reviewStateMatches = React.useCallback((it) => {
    const st = (it.status || it.verification_status || it.attributes?.status ||
      it.attributes?.verification_status || '').toString().toLowerCase();
    const rec = approvalStates && approvalStates[it.id];
    for (const f of reviewFilter) {
      if (f === 'archived' && isArchivedItem(it)) return true;
      if (f === 'archive_proposed' && st === 'archive_proposed') return true;
      if (f === 'needs_review' && openCommentItems && openCommentItems.has(it.id)) return true;
      if ((f === 'unverified' || f === 'unapproved') && rec && rec.state === 'pending') return true;
      if (f === 'not_approved' && rec && rec.state === 'not_approved') return true;
    }
    return false;
  }, [reviewFilter, openCommentItems, approvalStates]);

  // Per-row predicate: passes the active type filter, review-state filter AND text query.
  const matchItem = React.useCallback((it) => {
    if (typeActive) {
      const isC = isContainerItem(it);
      const kind = isC ? 'container' : 'requirement';
      if (!typeFilter.includes(kind)) return false;
    }
    if (typeIdActive && it.type_id !== typeIdFilter) return false;
    if (reviewActive && !reviewStateMatches(it)) return false;
    if (!q) return true;
    const hay = `${it.title || ''} ${humanId(it)} ${it.display_id || ''} ${it.id || ''}`.toLowerCase();
    return hay.includes(q);
  }, [q, typeActive, typeFilter, typeIdActive, typeIdFilter, reviewActive, reviewStateMatches]);

  const subtreeMatches = React.useCallback((it) => {
    if (matchItem(it)) return true;
    return (it.children || []).some(subtreeMatches);
  }, [matchItem]);

  // Flatten honouring expand state; when a filter is active, hide branches that
  // contain no match. Under lazy loading an expanded container whose children aren't
  // loaded yet emits a placeholder row and is queued for fetching (`needsLoad`).
  const needsLoad = [];
  const flatten = (list, depth, out) => {
    for (const it of list || []) {
      // #380: the trailing "load more" node is a control, not a requirement — render it
      // as a plain sibling row, and hide it while a filter/search is narrowing the tree
      // (paging is meaningless against a client-side filter).
      if (it && it._kind === 'load_more') {
        if (!filterActive && !searchActive) out.push({ item: it, depth, isContainer: false, open: false });
        continue;
      }
      if (filterActive && !subtreeMatches(it)) continue;
      const kids = it.children || [];
      const isContainer = isContainerItem(it);
      // #350: when a filter is active over an EAGERLY-loaded tree (the whole subtree
      // is present in memory), force every container open so matches at ANY depth are
      // surfaced without the user having to expand each branch by hand. Otherwise the
      // review-state filter only ever saw the handful of lazily-loaded nodes.
      const open = expanded.includes(it.id) || (eager && filterActive && !eagerCollapsedIds.includes(it.id));
      out.push({ item: it, depth, isContainer, open });
      if (isContainer && open) {
        if (kids.length) {
          flatten(kids, depth + 1, out);
        } else if (hasKids(it) && !searchActive && !eager) {
          needsLoad.push(it);
          out.push({ loadingFor: it.id, depth: depth + 1 });
        }
      }
    }
    return out;
  };

  const rows = flatten(items, 0, []);

  // #169: the keyboard-navigable rows in visible (flattened) order — real treeitems only,
  // excluding the loading placeholder and the "load more" control (they aren't treeitems).
  const treeRows = rows.filter((r) => r.item && !r.loadingFor && r.item._kind !== 'load_more');
  const treeIndexById = new Map(treeRows.map((r, i) => [r.item.id, i]));
  // #817: hand that same order upward. Compared as a joined string rather than by
  // reference, because `rows` is rebuilt on every render and a new array each time would
  // loop the parent's state update forever.
  const visibleOrderKey = treeRows.map((r) => r.item.id).join('\u0000');
  const lastPublishedOrder = React.useRef(null);
  React.useEffect(() => {
    if (!onVisibleOrderChange) return;
    if (lastPublishedOrder.current === visibleOrderKey) return;
    lastPublishedOrder.current = visibleOrderKey;
    onVisibleOrderChange(visibleOrderKey === '' ? [] : visibleOrderKey.split('\u0000'));
  }, [visibleOrderKey, onVisibleOrderChange]);
  // #169: ids of nodes that have visible child rows immediately following them — these get
  // a role="group" wrapper around those children (built in `renderForest`) and an
  // aria-owns pointer so the flat DOM still exposes real parent→children tree structure.
  const groupedIds = new Set();
  for (let k = 0; k < rows.length - 1; k += 1) {
    if (rows[k + 1].depth > rows[k].depth && rows[k].item?.id) groupedIds.add(rows[k].item.id);
  }
  // #169: the roving-tabindex target. Prefer an explicit keyboard/focus choice; else the
  // first selected visible row; else the first row — so there's always one tab stop.
  const effectiveActiveId = (activeId && treeIndexById.has(activeId))
    ? activeId
    : (selected.find((s) => treeIndexById.has(s)) || treeRows[0]?.item?.id || null);

  // #169: move keyboard focus (and the roving tab stop) to a node by id.
  const focusRow = (id) => {
    setActiveId(id);
    const el = rowRefs.current.get(id);
    if (el) el.focus();
  };

  // Keyboard expand/collapse re-renders the row list, and the re-render can drop real
  // DOM focus to <body> (observed: tree-a11y e2e — after ArrowLeft collapsed a
  // container, the row kept tabindex=0 but document.activeElement was <body>, so the
  // next ArrowRight never reached onRowKeyDown and the APG pattern went dead). Effects
  // run AFTER the commit, so re-anchoring here survives whatever remounting the
  // expansion change caused. Guarded: only when focus genuinely fell to <body> — never
  // steal focus from an element the user (or another component) legitimately holds.
  const pendingFocusRef = React.useRef(null);
  React.useEffect(() => {
    const id = pendingFocusRef.current;
    if (!id) return;
    pendingFocusRef.current = null;
    const el = rowRefs.current.get(id);
    if (el && (document.activeElement === document.body || document.activeElement === null)) {
      el.focus();
    }
  });

  // #169: WAI-ARIA "tree" keyboard support. Up/Down move between visible nodes;
  // Right expands a collapsed container then descends; Left collapses an open container
  // then ascends to the parent; Home/End jump to the ends; Enter/Space activate (select +
  // open) the node — mirroring a plain mouse click. Mouse expand/collapse & selection are
  // untouched (this only ADDS keyboard + semantics).
  const onRowKeyDown = (e, row) => {
    const id = row.item.id;
    const ti = treeIndexById.get(id);
    if (ti === undefined) return;
    switch (e.key) {
      case 'ArrowDown': {
        e.preventDefault();
        const n = treeRows[ti + 1];
        if (n) focusRow(n.item.id);
        break;
      }
      case 'ArrowUp': {
        e.preventDefault();
        const p = treeRows[ti - 1];
        if (p) focusRow(p.item.id);
        break;
      }
      case 'ArrowRight': {
        e.preventDefault();
        if (row.isContainer && !row.open) { setOpen(id, true); pendingFocusRef.current = id; }
        else if (row.isContainer && row.open) {
          const n = treeRows[ti + 1];
          if (n && n.depth > row.depth) focusRow(n.item.id);
        }
        break;
      }
      case 'ArrowLeft': {
        e.preventDefault();
        if (row.isContainer && row.open) { setOpen(id, false); pendingFocusRef.current = id; }
        else {
          for (let k = ti - 1; k >= 0; k -= 1) {
            if (treeRows[k].depth < row.depth) { focusRow(treeRows[k].item.id); break; }
          }
        }
        break;
      }
      case 'Home': {
        e.preventDefault();
        if (treeRows[0]) focusRow(treeRows[0].item.id);
        break;
      }
      case 'End': {
        e.preventDefault();
        const last = treeRows[treeRows.length - 1];
        if (last) focusRow(last.item.id);
        break;
      }
      case 'Enter':
      case ' ': {
        e.preventDefault();
        onSelectedChange?.([id]);
        onItemClick?.(id);
        anchorRef.current = id;
        break;
      }
      default:
        break;
    }
  };

  // Fetch children for any expanded-but-unloaded branch (idempotent in the hook).
  //
  // `needsLoad` is rebuilt during every render and `onLoadChildren` is useTreeState's
  // `loadChildren` (whose identity tracks `searchResult`) — listing either would turn
  // this into a fire-on-every-render fetch pump against the tree API. `[rows.length,
  // expanded]` is the deliberate trigger: "the visible row set changed". Both values are
  // read through refs at run time, so the effect always uses the CURRENT queue and the
  // CURRENT loader while the trigger set stays exactly as it was.
  // (Declared before the consumer so it runs first in the same commit.)
  const needsLoadRef = React.useRef(needsLoad);
  const onLoadChildrenRef = React.useRef(onLoadChildren);
  React.useEffect(() => {
    needsLoadRef.current = needsLoad;
    onLoadChildrenRef.current = onLoadChildren;
  });
  React.useEffect(() => {
    const loader = onLoadChildrenRef.current;
    if (!loader) return;
    needsLoadRef.current.forEach((it) => loader(it));
  }, [rows.length, expanded]);

  // #655 — deep-link auto-reveal. A far-down row can be selected (and its ancestors
  // auto-expanded by useTreeState) while still being scrolled OUT of the visible
  // viewport — the tree isn't virtualized any more, but a long list is still a
  // scrollable box, so "in the DOM" and "on screen" are not the same thing. Re-arm
  // whenever the single-selection target changes, then re-check on every render
  // (not just once) because a deep link's ancestors/children can still be loading
  // lazily — the target row may not exist in `rowRefs` for a few renders yet.
  const scrollTargetRef = React.useRef(null);
  const scrolledForRef = React.useRef(null);
  React.useEffect(() => {
    const id = selected.length === 1 ? selected[0] : null;
    if (id !== scrollTargetRef.current) {
      scrollTargetRef.current = id;
      scrolledForRef.current = null;
    }
  }, [selected]);
  React.useEffect(() => {
    const id = scrollTargetRef.current;
    if (!id || scrolledForRef.current === id) return;
    const el = rowRefs.current.get(id);
    if (el) {
      // jsdom (Jest) doesn't implement scrollIntoView unless a test polyfills it —
      // guard so mounting a pre-selected tree in an unrelated test suite doesn't
      // throw. Real browsers always have it.
      if (typeof el.scrollIntoView === 'function') el.scrollIntoView({ block: 'center' });
      scrolledForRef.current = id;
    }
    // No deps: intentionally re-checked on every render so a row that mounts a
    // few renders after the selection change (lazy-loaded ancestor/child) still
    // gets scrolled to once it exists — see scrolledForRef for the "done" latch.
  });

  // Expand/collapse a container by id. Shared by the mouse gutter and the keyboard
  // (ArrowRight/ArrowLeft) handlers so both paths queue lazy children identically.
  const setOpen = (id, willOpen) => {
    if (willOpen === expanded.includes(id)) return; // already in the requested state
    const next = willOpen ? [...expanded, id] : expanded.filter((x) => x !== id);
    onExpandedChange?.(next);
    if (willOpen && onLoadChildren) {
      const it = findRow(id);
      if (it) onLoadChildren(it);
    }
  };
  const toggle = (id, e) => {
    e.stopPropagation();
    setOpen(id, !expanded.includes(id));
  };

  // Locate a node anywhere in the (possibly lazily-loaded) tree by id.
  const findRow = (id) => {
    const walk = (list) => {
      for (const it of list || []) {
        if (it.id === id) return it;
        const hit = walk(it.children || []);
        if (hit) return hit;
      }
      return null;
    };
    return walk(items);
  };

  const click = (e, id, index) => {
    if (editingId) return;
    // #351: virtual project group nodes are now SELECTABLE (a click on the title/row
    // opens the ProjectDetails view). Expand/collapse is handled by the leading gutter
    // (chevron+folder icon), so a plain row click selects rather than toggles.
    const node = findRow(id);
    if (node && node._kind === 'load_more') { onLoadMore?.(node); return; }
    if (node && node._kind === 'project') {
      onSelectedChange?.([id]);
      onItemClick?.(id);
      anchorRef.current = id;
      return;
    }
    if (!singleSelect && (e.metaKey || e.ctrlKey)) {
      const next = selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id];
      onSelectedChange?.(next);
      anchorRef.current = id;
    } else if (!singleSelect && e.shiftKey && anchorRef.current) {
      const ids = rows.map((r) => r.item?.id).filter(Boolean);
      const a = ids.indexOf(anchorRef.current);
      const b = index;
      const [lo, hi] = a < b ? [a, b] : [b, a];
      const range = ids.slice(lo, hi + 1);
      onSelectedChange?.(Array.from(new Set([...selected, ...range])));
    } else {
      onSelectedChange?.([id]);
      onItemClick?.(id);
      anchorRef.current = id;
    }
  };

  const startEdit = (item, e) => {
    if (!onRename || item._kind === 'project') return;
    e?.stopPropagation();
    setEditingId(item.id);
    setEditValue(item.title || '');
  };
  const commitEdit = (item) => {
    const v = editValue.trim();
    setEditingId(null);
    if (v && v !== (item.title || '')) onRename?.(item.id, v);
  };

  // #408: classify where in the row the pointer is → nest ('inside', containers only)
  // vs reorder ('before'/'after'). The middle band of a container nests; the top/bottom
  // edges (and the whole of a leaf) reorder relative to that row.
  const dropModeFor = (e, isContainer) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const frac = rect.height ? (e.clientY - rect.top) / rect.height : 0.5;
    if (isContainer && frac > 0.28 && frac < 0.72) return 'inside';
    return frac < 0.5 ? 'before' : 'after';
  };

  const onDrop = (e, targetId) => {
    e.preventDefault();
    setDropInfo(null);
    const draggedId = e.dataTransfer.getData('text/plain');
    if (!draggedId || draggedId === targetId) return;
    const plan = planDrop(items, draggedId, targetId);
    if (plan && onMoveItem) onMoveItem(plan.id, plan.payload);
  };

  const bgMenu = (e) => {
    if (e.target.closest('.trow')) return; // row handles its own
    e.preventDefault();
    onBackgroundContextMenu?.(e);
  };

  return (
    <div data-testid="requirements-tree-grid" style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      <div
        className="treebox"
        data-testid="tree-grid-body"
        style={{ flex: 1, minHeight: 0 }}
        onContextMenu={bgMenu}
        role="tree"
        // #793 H: `tree` is an interactive role and must be focusable. It must NOT be a
        // tab stop, though — the rows implement APG's ROVING tabindex (see the row's
        // `tabIndex={effectiveActiveId === item.id ? 0 : -1}` below), and a tabbable
        // tree container would add a second stop and break that pattern.
        tabIndex={-1}
        aria-label="Requirements tree"
        aria-multiselectable={singleSelect ? undefined : true}
      >
        {rows.length === 0 ? (
          <div className="t-body muted" style={{ padding: 12 }}>
            {(filterActive || searchActive) ? 'No items found.' : 'No requirements yet.'}
          </div>
        ) : renderForest(rows)}
      </div>
    </div>
  );

  // #169: render a single flattened row (unchanged markup + behaviour). Declared as a
  // hoisted function so `renderForest` (below) can call it from the JSX above.
  function renderRow(row, index) {
          // Placeholder row for a branch whose children are still loading — or whose
          // load FAILED.
          //
          // Owner report, 2026-07-28: "if the tree view fails to load, it needs to be
          // more explicit 'Failed to load' with a refresh button". It used to render a
          // bare "…" in the failure case, which is what the owner's screenshot shows:
          // silent, permanent, and indistinguishable from an empty container. The
          // snackbar that fired at the moment of failure was long gone. A failed branch
          // now SAYS so and carries its own retry.
          if (row.loadingFor) {
            const isLoading = loadingIds && loadingIds.has && loadingIds.has(row.loadingFor);
            const hasFailed = !isLoading && failedChildIds
              && failedChildIds.has && failedChildIds.has(row.loadingFor);
            if (hasFailed) {
              return (
                <div
                  key={`failed-${row.loadingFor}`}
                  className="trow"
                  data-testid={`load-failed-${row.loadingFor}`}
                  style={{ paddingLeft: 6 + row.depth * 13, gap: 8 }}
                >
                  <span className="indent" style={{ width: 13 }} />
                  <span className="t-body" style={{ color: 'var(--error)' }}>Failed to load</span>
                  <button
                    type="button"
                    className="linkbtn"
                    data-testid={`retry-children-${row.loadingFor}`}
                    onClick={(e) => {
                      // The row sits inside the tree's click/selection handling; a retry
                      // is not a selection.
                      e.stopPropagation();
                      if (onRetryChildren) onRetryChildren(row.loadingFor);
                    }}
                  >
                    Retry
                  </button>
                </div>
              );
            }
            return (
              <div
                key={`loading-${row.loadingFor}`}
                className="trow muted"
                data-testid={`loading-${row.loadingFor}`}
                style={{ paddingLeft: 6 + row.depth * 13, opacity: 0.7, fontStyle: 'italic' }}
              >
                <span className="indent" style={{ width: 13 }} />
                <span className="t-body">{isLoading ? 'Loading…' : '…'}</span>
              </div>
            );
          }
          const { item, depth, isContainer, open } = row;
          // #380: the paginated level's "load more" control — a clickable row that fetches
          // the next page (never selectable / draggable / renamable).
          if (item && item._kind === 'load_more') {
            const busy = loadingIds && loadingIds.has && loadingIds.has(item.id);
            const label = busy
              ? 'Loading…'
              : `Load more${item.remaining ? ` (${item.remaining} more)` : ''}…`;
            return (
              <div
                key={item.id}
                className="trow"
                role="button"
                tabIndex={0}
                data-testid={`load-more-${item.parentKey}`}
                onClick={() => { if (!busy) onLoadMore?.(item); }}
                onKeyDown={(e) => { if (!busy && (e.key === 'Enter' || e.key === ' ')) onLoadMore?.(item); }}
                style={{ paddingLeft: 6 + depth * 13, cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.7 : 1 }}
              >
                <span className="indent" style={{ width: 13 }} />
                <span className="t-body" style={{ fontStyle: 'italic', fontWeight: 600 }}>{label}</span>
              </div>
            );
          }
          const sel = selected.includes(item.id);
          const isProject = item._kind === 'project';
          const archived = isArchivedItem(item);
          // #581 (4th surface): containers ARE approvable (the Breakdown pill
          // renders one for them, feedback.set_approval doesn't restrict by item
          // type) — a container approved in the Breakdown must show a dot here too.
          // Only a virtual project-group row (never itself approvable) is excluded.
          const dot = !isProject ? approvalDot(item, approvalStates) : null;
          const isDrop = dropInfo && dropInfo.id === item.id;
          const dropMode = isDrop ? dropInfo.mode : null;
          const editing = editingId === item.id;
          // Project group + ancestor-scaffolding nodes aren't draggable/renamable/selectable
          // items; matched search hits get a subtle emphasis.
          const isMatch = item.matched === true;
          return (
            <div
              key={item.id}
              data-testid={`row-${item.id}`}
              className={`trow${sel ? ' sel' : ''}${isContainer && open ? ' open' : ''}${isProject ? ' project-row' : ''}`}
              style={{
                // #357: the per-depth indent is NOT applied to the row itself for
                // containers — it lives INSIDE the clickable gutter (below) so the
                // whole left region, indent included, toggles expand rather than
                // selecting. Leaves keep the indent on the row (nothing to toggle).
                paddingLeft: 6,
                position: 'relative', // #408: anchors the drop insertion line
                ...(isProject ? { fontWeight: 600 } : null),
                ...(archived ? { opacity: 0.55, textDecoration: 'line-through' } : null),
                ...(isMatch ? { background: 'color-mix(in srgb, var(--accent) 12%, transparent)' } : null),
                // #408: nesting into a container → tinted highlight + outline.
                ...(dropMode === 'inside'
                  ? { outline: '1.5px solid var(--accent)', outlineOffset: -1,
                      background: 'color-mix(in srgb, var(--accent) 14%, transparent)' }
                  : null),
              }}
              title={archived ? 'Archived' : undefined}
              onClick={(e) => click(e, item.id, index)}
              onContextMenu={(e) => onContextMenu?.(e, item)}
              onKeyDown={(e) => onRowKeyDown(e, row)}
              onFocus={() => { if (activeId !== item.id) setActiveId(item.id); }}
              ref={(el) => { if (el) rowRefs.current.set(item.id, el); else rowRefs.current.delete(item.id); }}
              role="treeitem"
              aria-selected={sel}
              aria-level={depth + 1}
              {...(isContainer ? { 'aria-expanded': open } : null)}
              {...(groupedIds.has(item.id) ? { 'aria-owns': `treegrp-${item.id}` } : null)}
              tabIndex={effectiveActiveId === item.id ? 0 : -1}
              draggable={!isMobile && !editing && !isProject}
              onDragStart={(e) => { e.dataTransfer.setData('text/plain', item.id); e.dataTransfer.effectAllowed = 'move'; }}
              onDragOver={(e) => {
                if (isProject) return;
                e.preventDefault();
                e.dataTransfer.dropEffect = 'move';
                setDropInfo({ id: item.id, mode: dropModeFor(e, isContainer) });
              }}
              onDragLeave={() => setDropInfo((d) => (d && d.id === item.id ? null : d))}
              onDrop={(e) => (isProject ? null : onDrop(e, item.id))}
            >
              {/* #408: insertion line for a reorder drop (before/after this row). */}
              {(dropMode === 'before' || dropMode === 'after') && (
                <span
                  aria-hidden="true"
                  style={{
                    position: 'absolute', left: 6, right: 6, height: 2,
                    background: 'var(--accent)', borderRadius: 2, pointerEvents: 'none',
                    ...(dropMode === 'before' ? { top: -1 } : { bottom: -1 }),
                  }}
                />
              )}
              {/* #357: the whole leading gutter — the per-depth INDENT + chevron +
                  folder icon + id chip — toggles expand/collapse for containers, so a
                  tap anywhere LEFT of the title opens the folder rather than selecting
                  the item (the small chevron was an unforgiving hit target on mobile,
                  and the indent/icon/id previously fell through to the row's select).
                  Leaf rows keep a plain indent + icon + id that fall through to the
                  row's select handler (a leaf has nothing to toggle). */}
              {isContainer ? (
                <span
                  className="trow-gutter"
                  data-testid={`expand-${item.id}`}
                  onClick={(e) => toggle(item.id, e)}
                  // #793 H: this used to claim `role="button"` + `aria-label="Expand"`,
                  // which was WRONG twice over — it was a button you could neither tab
                  // to nor activate with a key, AND it announced a redundant nested
                  // control inside a `treeitem` that already carries `aria-expanded`
                  // and already handles ArrowRight/ArrowLeft (see `onRowKeyDown`, the
                  // full APG treeview pattern). The correct model is: the gutter is a
                  // POINTER-ONLY enlarged hit target for the chevron (#357), and the
                  // semantics live on the row. `role="presentation"` says exactly that.
                  // A real <button> here would be worse still — it would inject a
                  // second tab stop into the row and break the roving-tabindex pattern.
                  role="presentation"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer', flex: '0 0 auto' }}
                >
                  {depth > 0 && <span className="indent" style={{ width: depth * 13, flex: '0 0 auto' }} />}
                  <Ic name="chev" className="chev" />
                  <Ic name="folder" className="tic" />
                  {!isProject && <span className="id">{showHumanIds ? humanId(item) : rawId(item)}</span>}
                </span>
              ) : (
                <>
                  {depth > 0 && <span className="indent" style={{ width: depth * 13, flex: '0 0 auto' }} />}
                  <span className="indent" style={{ width: 13 }} />
                  <Ic name="doc" className="tic" />
                  <span className="id">{showHumanIds ? humanId(item) : rawId(item)}</span>
                </>
              )}
              {editing ? (
                <input
                  data-testid={`edit-${item.id}-title`}
                  className="ttl"
                  ref={editFocusRef}
                  value={editValue}
                  onChange={(e) => setEditValue(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') { e.preventDefault(); commitEdit(item); }
                    else if (e.key === 'Escape') { e.preventDefault(); setEditingId(null); }
                  }}
                  onBlur={() => commitEdit(item)}
                  style={{ flex: 1, minWidth: 0, font: 'inherit', border: '1px solid var(--accent)', borderRadius: 4, padding: '0 4px', background: 'var(--base)', color: 'inherit' }}
                />
              ) : isProject ? (
                // Virtual project-group nodes only expand/collapse (see `click`
                // above) — they have no item deep-link, so this stays plain text.
                <span
                  className="ttl"
                  data-testid={`cell-${item.id}-title`}
                  title={item.title}
                >
                  {item.title || 'Untitled'}
                </span>
              ) : (
                // Issue #339: a real <a href> so a middle-click opens the item in
                // a new tab. `draggable={false}` stops the browser's native
                // link-drag from hijacking the row's own HTML5 drag-reorder
                // (which starts from the ancestor `.trow`'s `draggable` attribute).
                // The click handler only ever preventDefault()s a PLAIN left
                // click — ctrl/⌘/shift are left alone so they keep this tree's
                // existing multi-select/range-select meaning (and, being real
                // modified clicks on an anchor, the browser also opens a
                // background tab, matching how any other link behaves).
                <a
                  className="ttl"
                  data-testid={`cell-${item.id}-title`}
                  title={item.title}
                  href={itemHashHref(item.id)}
                  draggable={false}
                  onDoubleClick={(e) => startEdit(item, e)}
                  onClick={(e) => { if (isPlainLeftClick(e)) e.preventDefault(); }}
                >
                  {item.title || 'Untitled'}
                </a>
              )}
              {statusDots && dot && (() => {
                const rec = approvalStates && approvalStates[item.id];
                const glyph = (rec && APPROVAL_DOT_GLYPH[rec.state]) || '';
                const label = approvalTitleFor(item, approvalStates);
                return (
                  <span
                    className={`dot ${dot}`}
                    title={label}
                    aria-label={label}
                    role="img"
                    data-testid={`approval-dot-${item.id}`}
                    style={{
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                      // Slightly larger than the shared 7px .dot so the glyph (the
                      // non-colour channel WCAG 1.4.1 requires) is actually legible —
                      // still reads as "a dot", just a touch bigger.
                      width: 10, height: 10, fontSize: 7, lineHeight: 1, fontWeight: 900,
                      color: '#fff',
                    }}
                  >
                    {glyph}
                  </span>
                );
              })()}
              {/* #342 Phase 2 / #568/#569: a DISTINCT glyph (not a second coloured
                  dot — that was the "mystery orange dot" #568 reported, easily
                  confused with the approval dot above) marking items with open
                  review comments. Carries a title/aria-label (never colour-only). */}
              {openCommentItems && openCommentItems.has(item.id) && (
                <span
                  data-testid={`open-comment-dot-${item.id}`}
                  title="Has open review comments"
                  aria-label="Has open review comments"
                  style={{
                    display: 'inline-flex', alignItems: 'center', flex: '0 0 auto',
                    marginLeft: 6, color: 'var(--warning)',
                  }}
                >
                  <Ic name="comment" sm />
                </span>
              )}
            </div>
          );
  }

  // #169: rebuild the flat, virtualized row list into a real ARIA tree. Each container's
  // visible children are wrapped in a role="group" whose parent treeitem aria-owns it —
  // `display: contents` keeps the wrapper layout-neutral so the existing flex-column look
  // and per-depth indentation are untouched. A flattened DOM otherwise can't expose
  // parent→children structure to assistive tech.
  function renderForest(allRows) {
    let i = 0;
    const build = (parentDepth) => {
      const out = [];
      while (i < allRows.length) {
        const r = allRows[i];
        if (r.depth < parentDepth) break;
        const idx = i;
        i += 1;
        const el = renderRow(r, idx);
        const key = r.item?.id || r.loadingFor || `row-${idx}`;
        if (i < allRows.length && allRows[i].depth > r.depth) {
          const kids = build(r.depth + 1);
          out.push(
            <React.Fragment key={`grp-${key}`}>
              {el}
              <div role="group" id={`treegrp-${key}`} style={{ display: 'contents' }}>{kids}</div>
            </React.Fragment>,
          );
        } else {
          out.push(el);
        }
      }
      return out;
    };
    return build(0);
  }
}

RequirementsTree.propTypes = {
  items: PropTypes.array,
  selected: PropTypes.array,
  expanded: PropTypes.array,
  onSelectedChange: PropTypes.func,
  onVisibleOrderChange: PropTypes.func,
  onExpandedChange: PropTypes.func,
  onItemClick: PropTypes.func,
  onContextMenu: PropTypes.func,
  onMoveItem: PropTypes.func,
  onBackgroundContextMenu: PropTypes.func,
  filterText: PropTypes.string,
  typeFilter: PropTypes.array,
  onRename: PropTypes.func,
  onLoadChildren: PropTypes.func,
  onLoadMore: PropTypes.func,
  loadingIds: PropTypes.object,
  failedChildIds: PropTypes.object,
  onRetryChildren: PropTypes.func,
  searchActive: PropTypes.bool,
  openCommentItems: PropTypes.instanceOf(Set),
  reviewFilter: PropTypes.array,
  approvalStates: PropTypes.object,
  eager: PropTypes.bool,
  typeIdFilter: PropTypes.string,
  eagerCollapsedIds: PropTypes.array,
  singleSelect: PropTypes.bool,
};
